/**
 * Utility functions for working with SVG files.
 */
import { noise2DNormalized } from "./perlinNoise";
import {
    startFrameBudget,
    yieldIfFrameExhausted,
    yieldToMain as schedulerYieldToMain,
    type FrameBudget,
} from "./cooperativeScheduler";

/**
 * Extracts viewBox dimensions from an SVG string.
 *
 * @param svgString - The SVG content as a string
 * @returns Object with width, height, and viewBox, or null if not found
 */
export function extractViewBox(svgString: string): {
    viewBox: string;
    width?: number;
    height?: number;
} | null {
    const viewBoxMatch = svgString.match(/viewBox=["']([^"']+)["']/i);
    const widthMatch = svgString.match(/width=["']([^"']+)["']/i);
    const heightMatch = svgString.match(/height=["']([^"']+)["']/i);

    if (!viewBoxMatch) {
        return null;
    }

    const viewBox = viewBoxMatch[1];
    const width = widthMatch ? parseFloat(widthMatch[1]) : undefined;
    const height = heightMatch ? parseFloat(heightMatch[1]) : undefined;

    return { viewBox, width, height };
}

/**
 * Validates if a string is valid SVG content.
 *
 * @param content - The content to validate
 * @returns True if the content appears to be valid SVG
 */
export function isValidSvg(content: string): boolean {
    return /<svg[\s>]/.test(content.trim());
}

/**
 * Gets the dimensions from an SVG element or string.
 *
 * @param svg - Either an SVG element or SVG string content
 * @returns Object with width and height, or null if not available
 */
export function getSvgDimensions(
    svg: SVGSVGElement | string
): { width: number; height: number } | null {
    if (typeof svg === "string") {
        const viewBoxInfo = extractViewBox(svg);
        if (viewBoxInfo?.viewBox) {
            const [, , w, h] = viewBoxInfo.viewBox.split(/\s+/).map(parseFloat);
            if (!isNaN(w) && !isNaN(h)) {
                return { width: w, height: h };
            }
        }
        if (viewBoxInfo?.width && viewBoxInfo?.height) {
            return {
                width: viewBoxInfo.width,
                height: viewBoxInfo.height,
            };
        }
        return null;
    }

    // For SVG element
    const viewBox = svg.viewBox.baseVal;
    if (viewBox.width && viewBox.height) {
        return { width: viewBox.width, height: viewBox.height };
    }

    const width = svg.width.baseVal.value;
    const height = svg.height.baseVal.value;
    if (width && height) {
        return { width, height };
    }

    return null;
}

function escapeXmlAttrValue(s: string): string {
    return s.replace(/[&"]/g, (c) => (c === "&" ? "&amp;" : "&quot;"));
}

/**
 * Wrap object SVG fragment (markup in the object's root viewBox space) in a
 * nested `svg` so it keeps its coordinate system when placed inside another SVG
 * (e.g. object fill pack). `width` / `height` are in the host SVG's user units.
 */
export function wrapSvgObjectInnerForHostViewport(
    innerMarkup: string,
    viewBox: string,
    width: number,
    height: number,
): string {
    const safeVb = escapeXmlAttrValue(viewBox.trim());
    const w = Number.isFinite(width) ? width : 0;
    const h = Number.isFinite(height) ? height : 0;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${safeVb}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet">${innerMarkup}</svg>`;
}

/**
 * Converts polygon points string to a path data string.
 *
 * @param points - Polygon points string (e.g., "100,100 200,100 200,200 100,200")
 * @returns Path data string (e.g., "M 100,100 L 200,100 L 200,200 L 100,200 Z")
 */
export function polygonPointsToPathData(points: string): string {
    if (!points || !points.trim()) return "";

    // Parse points - handle both comma-separated and space-separated formats
    const coords = points
        .trim()
        .split(/[\s,]+/)
        .map(parseFloat)
        .filter((n) => !isNaN(n));

    if (coords.length < 4 || coords.length % 2 !== 0) return "";

    // Build path data: M to first point, L to each subsequent point, Z to close
    let pathData = `M ${coords[0]},${coords[1]}`;
    for (let i = 2; i < coords.length; i += 2) {
        pathData += ` L ${coords[i]},${coords[i + 1]}`;
    }
    pathData += " Z";

    return pathData;
}

/**
 * Converts polyline points string to a path data string.
 *
 * @param points - Polyline points string (e.g., "100,100 200,100 200,200")
 * @returns Path data string (e.g., "M 100,100 L 200,100 L 200,200")
 */
export function polylinePointsToPathData(points: string): string {
    if (!points || !points.trim()) return "";

    // Parse points - handle both comma-separated and space-separated formats
    const coords = points
        .trim()
        .split(/[\s,]+/)
        .map(parseFloat)
        .filter((n) => !isNaN(n));

    if (coords.length < 4 || coords.length % 2 !== 0) return "";

    // Build path data: M to first point, L to each subsequent point (no Z - polylines don't close)
    let pathData = `M ${coords[0]},${coords[1]}`;
    for (let i = 2; i < coords.length; i += 2) {
        pathData += ` L ${coords[i]},${coords[i + 1]}`;
    }

    return pathData;
}

/**
 * Converts line attributes to a path data string.
 *
 * @param x1 - X coordinate of first point
 * @param y1 - Y coordinate of first point
 * @param x2 - X coordinate of second point
 * @param y2 - Y coordinate of second point
 * @returns Path data string (e.g., "M 100,100 L 200,200")
 */
export function lineToPathData(x1: number, y1: number, x2: number, y2: number): string {
    return `M ${x1},${y1} L ${x2},${y2}`;
}

/** Outline segment for Object-along-path style tools. */
export type PathPopulatePathSegment =
    | { kind: "d"; d: string }
    | { kind: "rect"; tag: string };

/**
 * Collect outline segments from path SVG markup (order: paths, polygons, polylines, lines, rects).
 */
export function extractPathPopulateSegments(svgContent: string): PathPopulatePathSegment[] {
    const segments: PathPopulatePathSegment[] = [];

    const pathMatches = [
        ...svgContent.matchAll(/<path[^>]*\sd=["']([^"']+)["'][^>]*>/gi),
    ];
    for (const m of pathMatches) {
        segments.push({ kind: "d", d: m[1] });
    }

    const polygonMatches = [
        ...svgContent.matchAll(/<polygon[^>]*\spoints=["']([^"']+)["'][^>]*>/gi),
    ];
    for (const match of polygonMatches) {
        const pathData = polygonPointsToPathData(match[1]);
        if (pathData) {
            segments.push({ kind: "d", d: pathData });
        }
    }

    const polylineMatches = [
        ...svgContent.matchAll(/<polyline[^>]*\spoints=["']([^"']+)["'][^>]*>/gi),
    ];
    for (const match of polylineMatches) {
        const pathData = polylinePointsToPathData(match[1]);
        if (pathData) {
            segments.push({ kind: "d", d: pathData });
        }
    }

    const lineMatches = [...svgContent.matchAll(/<line[^>]*>/gi)];
    for (const match of lineMatches) {
        const tag = match[0];
        const x1Match = tag.match(/x1=["']([^"']+)["']/i);
        const y1Match = tag.match(/y1=["']([^"']+)["']/i);
        const x2Match = tag.match(/x2=["']([^"']+)["']/i);
        const y2Match = tag.match(/y2=["']([^"']+)["']/i);

        if (x1Match && y1Match && x2Match && y2Match) {
            const x1 = parseFloat(x1Match[1]);
            const y1 = parseFloat(y1Match[1]);
            const x2 = parseFloat(x2Match[1]);
            const y2 = parseFloat(y2Match[1]);

            if (!isNaN(x1) && !isNaN(y1) && !isNaN(x2) && !isNaN(y2)) {
                segments.push({ kind: "d", d: lineToPathData(x1, y1, x2, y2) });
            }
        }
    }

    const rectMatches = [...svgContent.matchAll(/<rect[^>]*>/gi)];
    for (const match of rectMatches) {
        segments.push({ kind: "rect", tag: match[0] });
    }

    return segments;
}

/**
 * Sample positions along any SVGGeometryElement (path, rect, circle, …).
 * Requires a browser; rects use perimeter length per SVG2 geometry APIs.
 */
export function samplePointsAlongGeometry(
    geometry: SVGGeometryElement,
    distance: number,
    isClosed: boolean
): { x: number; y: number; angle: number; position: number }[] {
    if (distance <= 0) return [];

    const totalLength = geometry.getTotalLength();
    if (totalLength === 0) return [];

    const lookaheadDistance = Math.min(0.01, totalLength * 0.01);
    const minDistanceBetweenPoints = distance * 0.8;
    const maxLength = isClosed ? totalLength - distance * 0.5 : totalLength;
    let lastPoint: { x: number; y: number } | null = null;
    const seenPositions = new Set<string>();
    const positionEpsilon = distance * 0.1;
    const points: { x: number; y: number; angle: number; position: number }[] = [];

    for (let cur = 0; cur < maxLength; cur += distance) {
        const p = geometry.getPointAtLength(cur);
        const position = cur / totalLength;

        const roundedX = Math.round(p.x / positionEpsilon) * positionEpsilon;
        const roundedY = Math.round(p.y / positionEpsilon) * positionEpsilon;
        const positionKey = `${roundedX},${roundedY}`;

        if (seenPositions.has(positionKey)) {
            continue;
        }

        if (lastPoint) {
            const dx = p.x - lastPoint.x;
            const dy = p.y - lastPoint.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDistanceBetweenPoints) {
                continue;
            }
        }

        seenPositions.add(positionKey);

        let aheadLength = cur + lookaheadDistance;

        if (aheadLength >= totalLength) {
            if (isClosed) {
                aheadLength = aheadLength - totalLength;
            } else {
                aheadLength = Math.max(0, cur - lookaheadDistance);
                const behind = geometry.getPointAtLength(aheadLength);
                const angle = Math.atan2(p.y - behind.y, p.x - behind.x);
                points.push({ x: p.x, y: p.y, angle, position });
                lastPoint = { x: p.x, y: p.y };
                continue;
            }
        }

        const ahead = geometry.getPointAtLength(aheadLength);
        const angle = Math.atan2(ahead.y - p.y, ahead.x - p.x);
        points.push({ x: p.x, y: p.y, angle, position });
        lastPoint = { x: p.x, y: p.y };
    }

    return points;
}

/**
 * Like {@link samplePointsAlongGeometry}, but yields cooperatively so dense paths
 * cannot freeze the tab. Uses the shared frame budget when provided, otherwise
 * creates a local one.
 */
export async function samplePointsAlongGeometryAsync(
    geometry: SVGGeometryElement,
    distance: number,
    isClosed: boolean,
    options?: { signal?: AbortSignal; budget?: FrameBudget },
): Promise<{ x: number; y: number; angle: number; position: number }[]> {
    if (distance <= 0) return [];

    const signal = options?.signal;
    const budget = options?.budget ?? startFrameBudget();

    const totalLength = geometry.getTotalLength();
    if (totalLength === 0) return [];

    const lookaheadDistance = Math.min(0.01, totalLength * 0.01);
    const minDistanceBetweenPoints = distance * 0.8;
    const maxLength = isClosed ? totalLength - distance * 0.5 : totalLength;
    let lastPoint: { x: number; y: number } | null = null;
    const seenPositions = new Set<string>();
    const positionEpsilon = distance * 0.1;
    const points: { x: number; y: number; angle: number; position: number }[] =
        [];

    for (let cur = 0; cur < maxLength; cur += distance) {
        await yieldIfFrameExhausted(budget, signal);

        const p = geometry.getPointAtLength(cur);
        const position = cur / totalLength;

        const roundedX = Math.round(p.x / positionEpsilon) * positionEpsilon;
        const roundedY = Math.round(p.y / positionEpsilon) * positionEpsilon;
        const positionKey = `${roundedX},${roundedY}`;

        if (seenPositions.has(positionKey)) {
            continue;
        }

        if (lastPoint) {
            const dx = p.x - lastPoint.x;
            const dy = p.y - lastPoint.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDistanceBetweenPoints) {
                continue;
            }
        }

        seenPositions.add(positionKey);

        let aheadLength = cur + lookaheadDistance;

        if (aheadLength >= totalLength) {
            if (isClosed) {
                aheadLength = aheadLength - totalLength;
            } else {
                aheadLength = Math.max(0, cur - lookaheadDistance);
                const behind = geometry.getPointAtLength(aheadLength);
                const angle = Math.atan2(p.y - behind.y, p.x - behind.x);
                points.push({ x: p.x, y: p.y, angle, position });
                lastPoint = { x: p.x, y: p.y };
                continue;
            }
        }

        const ahead = geometry.getPointAtLength(aheadLength);
        const angle = Math.atan2(ahead.y - p.y, ahead.x - p.x);
        points.push({ x: p.x, y: p.y, angle, position });
        lastPoint = { x: p.x, y: p.y };
    }

    return points;
}

function transformAngleByCtm(ctm: DOMMatrix, angle: number): number {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const tx = cos * ctm.a + sin * ctm.c;
    const ty = cos * ctm.b + sin * ctm.d;
    return Math.atan2(ty, tx);
}

/**
 * Like samplePointsAlongGeometry, but maps each point into the coordinate system of the
 * nearest SVG viewport (root viewBox space), including ancestor &lt;g&gt; transforms.
 * The element must be connected to the document so getCTM() is defined.
 */
export function samplePointsAlongGeometryInViewport(
    geometry: SVGGeometryElement,
    distance: number,
    isClosed: boolean
): { x: number; y: number; angle: number; position: number }[] {
    const local = samplePointsAlongGeometry(geometry, distance, isClosed);
    const ctm = geometry.getCTM();
    if (!ctm) return local;
    return local.map((p) => {
        const tp = new DOMPoint(p.x, p.y).matrixTransform(ctm);
        return {
            x: tp.x,
            y: tp.y,
            angle: transformAngleByCtm(ctm, p.angle),
            position: p.position,
        };
    });
}

/**
 * Async variant of {@link samplePointsAlongGeometryInViewport} with cooperative
 * scheduling during sampling and CTM mapping.
 */
export async function samplePointsAlongGeometryInViewportAsync(
    geometry: SVGGeometryElement,
    distance: number,
    isClosed: boolean,
    options?: { signal?: AbortSignal; budget?: FrameBudget },
): Promise<{ x: number; y: number; angle: number; position: number }[]> {
    const local = await samplePointsAlongGeometryAsync(
        geometry,
        distance,
        isClosed,
        options,
    );
    const ctm = geometry.getCTM();
    if (!ctm) return local;

    const signal = options?.signal;
    const budget = options?.budget ?? startFrameBudget();
    const out: { x: number; y: number; angle: number; position: number }[] =
        [];

    for (let i = 0; i < local.length; i++) {
        await yieldIfFrameExhausted(budget, signal);
        const p = local[i];
        const tp = new DOMPoint(p.x, p.y).matrixTransform(ctm);
        out.push({
            x: tp.x,
            y: tp.y,
            angle: transformAngleByCtm(ctm, p.angle),
            position: p.position,
        });
    }
    return out;
}

/**
 * Move a sampled point along the same geometry by an arc-length offset (local px),
 * then map to viewport space like {@link samplePointsAlongGeometryInViewport}.
 * `base.position` must be the normalized length from the original sample (0–1).
 */
export function offsetPointAlongGeometryInViewport(
    geometry: SVGGeometryElement,
    base: { position: number },
    offsetPx: number,
    isClosed: boolean
): { x: number; y: number; angle: number; position: number } {
    const totalLength = geometry.getTotalLength();
    if (totalLength === 0) {
        return { x: 0, y: 0, angle: 0, position: 0 };
    }

    let cur = base.position * totalLength + offsetPx;
    if (isClosed) {
        cur = ((cur % totalLength) + totalLength) % totalLength;
    } else {
        cur = Math.max(0, Math.min(totalLength, cur));
    }

    const lookaheadDistance = Math.min(0.01, totalLength * 0.01);
    const p = geometry.getPointAtLength(cur);
    const position = cur / totalLength;

    let aheadLength = cur + lookaheadDistance;

    let angle: number;
    if (aheadLength >= totalLength) {
        if (isClosed) {
            aheadLength = aheadLength - totalLength;
            const ahead = geometry.getPointAtLength(aheadLength);
            angle = Math.atan2(ahead.y - p.y, ahead.x - p.x);
        } else {
            aheadLength = Math.max(0, cur - lookaheadDistance);
            const behind = geometry.getPointAtLength(aheadLength);
            angle = Math.atan2(p.y - behind.y, p.x - behind.x);
        }
    } else {
        const ahead = geometry.getPointAtLength(aheadLength);
        angle = Math.atan2(ahead.y - p.y, ahead.x - p.x);
    }

    const ctm = geometry.getCTM();
    if (!ctm) {
        return { x: p.x, y: p.y, angle, position };
    }
    const tp = new DOMPoint(p.x, p.y).matrixTransform(ctm);
    return {
        x: tp.x,
        y: tp.y,
        angle: transformAngleByCtm(ctm, angle),
        position,
    };
}

const PATH_POPULATE_SKIP_TAGS = new Set([
    "defs",
    "clippath",
    "mask",
    "pattern",
    "symbol",
    "marker",
    "title",
    "desc",
    "metadata",
    "style",
    "script",
]);

/**
 * Depth-first collection of path-like geometry in document order (skips defs-like subtrees).
 */
export function collectPathPopulateGeometry(
    root: SVGSVGElement
): { el: SVGGeometryElement; isClosed: boolean }[] {
    const out: { el: SVGGeometryElement; isClosed: boolean }[] = [];

    function walk(node: Element) {
        const tag = node.tagName.toLowerCase();
        if (PATH_POPULATE_SKIP_TAGS.has(tag)) {
            return;
        }

        if (node instanceof SVGPathElement) {
            const d = node.getAttribute("d");
            if (d?.trim()) {
                const t = d.trim();
                out.push({
                    el: node,
                    isClosed: t.endsWith("z") || t.endsWith("Z"),
                });
            }
        } else if (node instanceof SVGPolygonElement) {
            if (node.getAttribute("points")?.trim()) {
                out.push({ el: node, isClosed: true });
            }
        } else if (node instanceof SVGPolylineElement) {
            if (node.getAttribute("points")?.trim()) {
                out.push({ el: node, isClosed: false });
            }
        } else if (node instanceof SVGLineElement) {
            out.push({ el: node, isClosed: false });
        } else if (node instanceof SVGRectElement) {
            const w = node.width.baseVal.value;
            const h = node.height.baseVal.value;
            if (w > 0 && h > 0 && Number.isFinite(w) && Number.isFinite(h)) {
                out.push({ el: node, isClosed: true });
            }
        }

        for (let i = 0; i < node.children.length; i++) {
            walk(node.children[i]);
        }
    }

    walk(root);
    return out;
}

/**
 * Parse path SVG, attach it to the document briefly so getCTM() works, then run fn.
 */
export function withMountedPathSvg<T>(svgMarkup: string, fn: (root: SVGSVGElement) => T): T | null {
    if (typeof document === "undefined") return null;
    const doc = new DOMParser().parseFromString(svgMarkup, "image/svg+xml");
    const parsed = doc.documentElement;
    if (!(parsed instanceof SVGSVGElement)) return null;
    if (parsed.tagName.toLowerCase() === "parsererror") return null;

    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
        "position:fixed;left:0;top:0;width:512px;height:512px;opacity:0;pointer-events:none;z-index:-1;overflow:hidden";

    const imported = document.importNode(parsed, true) as SVGSVGElement;
    // Match width/height to viewBox so user units ≈ CSS px; getCTM() then aligns with viewBox coords.
    const vbAttr = imported.getAttribute("viewBox");
    if (vbAttr) {
        const vbParts = vbAttr.trim().split(/[\s,]+/).map(Number);
        if (
            vbParts.length === 4 &&
            vbParts.every((n) => Number.isFinite(n)) &&
            vbParts[2] > 0 &&
            vbParts[3] > 0
        ) {
            imported.setAttribute("width", String(vbParts[2]));
            imported.setAttribute("height", String(vbParts[3]));
        }
    }
    host.appendChild(imported);
    document.body.appendChild(host);
    try {
        return fn(imported);
    } finally {
        document.body.removeChild(host);
    }
}

/** @deprecated Prefer `yieldIfFrameExhausted` from `cooperativeScheduler` for hot loops. */
export function yieldToMain(): Promise<void> {
    return schedulerYieldToMain();
}

/**
 * Like {@link withMountedPathSvg}, but the callback may return a Promise; the host
 * stays mounted until it settles so async sampling can use live geometry APIs.
 */
export async function withMountedPathSvgAsync<T>(
    svgMarkup: string,
    fn: (root: SVGSVGElement) => T | Promise<T>,
): Promise<T | null> {
    if (typeof document === "undefined") return null;
    const doc = new DOMParser().parseFromString(svgMarkup, "image/svg+xml");
    const parsed = doc.documentElement;
    if (!(parsed instanceof SVGSVGElement)) return null;
    if (parsed.tagName.toLowerCase() === "parsererror") return null;

    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
        "position:fixed;left:0;top:0;width:512px;height:512px;opacity:0;pointer-events:none;z-index:-1;overflow:hidden";

    const imported = document.importNode(parsed, true) as SVGSVGElement;
    const vbAttr = imported.getAttribute("viewBox");
    if (vbAttr) {
        const vbParts = vbAttr.trim().split(/[\s,]+/).map(Number);
        if (
            vbParts.length === 4 &&
            vbParts.every((n) => Number.isFinite(n)) &&
            vbParts[2] > 0 &&
            vbParts[3] > 0
        ) {
            imported.setAttribute("width", String(vbParts[2]));
            imported.setAttribute("height", String(vbParts[3]));
        }
    }
    host.appendChild(imported);
    document.body.appendChild(host);
    try {
        const out = fn(imported);
        return await Promise.resolve(out);
    } finally {
        document.body.removeChild(host);
    }
}

/**
 * Parse a single &lt;rect …/&gt; tag and sample along its perimeter in **local** rect
 * coordinates only (ancestor &lt;g&gt; transforms are ignored). Prefer
 * collectPathPopulateGeometry + samplePointsAlongGeometryInViewport on a mounted tree.
 */
export function samplePathPointsFromRectTag(
    rectTag: string,
    distance: number
): { x: number; y: number; angle: number; position: number }[] {
    if (!rectTag || distance <= 0) return [];
    const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${rectTag}</svg>`;
    const doc = new DOMParser().parseFromString(wrapped, "image/svg+xml");
    const rect = doc.querySelector("rect");
    if (!rect) return [];
    return samplePointsAlongGeometry(rect, distance, true);
}

/**
 * Simple path sampler using the browser's SVGPathElement APIs.
 *
 * Note: this only works in a browser environment.
 */
export function samplePathPointsFromD(
    d: string,
    distance: number
): { x: number; y: number; angle: number; position: number }[] {
    if (!d || distance <= 0) return [];

    const svgNs = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNs, "svg");
    const path = document.createElementNS(svgNs, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);

    const isClosed = d.trim().endsWith("Z") || d.trim().endsWith("z");

    return samplePointsAlongGeometry(path, distance, isClosed);
}

export interface ShapeElement {
    type: string;
    originalElement: string;
    startIndex: number;
    endIndex: number;
    center: { x: number; y: number };
    attributes: Record<string, string>;
}

export type CurveShape = "linear" | "exponential" | "ease-in-out" | "quadratic";

/**
 * Extracts all shape elements from SVG content.
 */
export function extractShapeElements(svgContent: string): ShapeElement[] {
    const shapes: ShapeElement[] = [];

    // Extract rectangles
    const rectMatches = [
        ...svgContent.matchAll(/<rect([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of rectMatches) {
        const attrs = parseAttributes(match[1]);
        const center = calculateRectCenter(attrs);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "rect",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract circles
    const circleMatches = [
        ...svgContent.matchAll(/<circle([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of circleMatches) {
        const attrs = parseAttributes(match[1]);
        const center = calculateCircleCenter(attrs);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "circle",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract ellipses
    const ellipseMatches = [
        ...svgContent.matchAll(/<ellipse([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of ellipseMatches) {
        const attrs = parseAttributes(match[1]);
        const center = calculateEllipseCenter(attrs);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "ellipse",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract paths
    const pathMatches = [
        ...svgContent.matchAll(/<path([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of pathMatches) {
        const attrs = parseAttributes(match[1]);
        const d = attrs.d;
        if (!d) continue;
        const center = calculatePathCenter(d, attrs.transform);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "path",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract polygons
    const polygonMatches = [
        ...svgContent.matchAll(/<polygon([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of polygonMatches) {
        const attrs = parseAttributes(match[1]);
        const points = attrs.points;
        if (!points) continue;
        const center = calculatePolygonCenter(points, attrs.transform);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "polygon",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract polylines
    const polylineMatches = [
        ...svgContent.matchAll(/<polyline([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of polylineMatches) {
        const attrs = parseAttributes(match[1]);
        const points = attrs.points;
        if (!points) continue;
        const center = calculatePolylineCenter(points, attrs.transform);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "polyline",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    // Extract lines
    const lineMatches = [
        ...svgContent.matchAll(/<line([^>]*?)(?:\s*\/>|>)/gi),
    ];
    for (const match of lineMatches) {
        const attrs = parseAttributes(match[1]);
        const center = calculateLineCenter(attrs);
        if (center && match.index !== undefined) {
            shapes.push({
                type: "line",
                originalElement: match[0],
                startIndex: match.index,
                endIndex: match.index + match[0].length,
                center,
                attributes: attrs,
            });
        }
    }

    return shapes;
}

function parseAttributes(attrString: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    const regex = /(\w+(?:-\w+)*)=["']([^"']+)["']/gi;
    let match;
    while ((match = regex.exec(attrString)) !== null) {
        attrs[match[1]] = match[2];
    }
    return attrs;
}

/**
 * Applies SVG transform to a point.
 * Handles translate, rotate, scale, and matrix transforms.
 */
function applyTransformToPoint(
    point: { x: number; y: number },
    transform: string
): { x: number; y: number } {
    if (!transform || !transform.trim()) return point;

    // Parse transform string - can contain multiple transforms separated by spaces or commas
    // Matches: translate(100,50), rotate(45), scale(2), matrix(...)
    const transformRegex = /(\w+)\(([^)]+)\)/g;
    let match;
    let x = point.x;
    let y = point.y;

    while ((match = transformRegex.exec(transform)) !== null) {
        const type = match[1].toLowerCase();
        const values = match[2]
            .split(/[\s,]+/)
            .map(parseFloat)
            .filter((n) => !isNaN(n));

        switch (type) {
            case "translate": {
                const tx = values[0] || 0;
                const ty = values[1] || 0;
                x += tx;
                y += ty;
                break;
            }
            case "rotate": {
                const angle = (values[0] || 0) * (Math.PI / 180); // Convert to radians
                const cx = values[1] || 0;
                const cy = values[2] || 0;
                // Translate to origin, rotate, translate back
                const dx = x - cx;
                const dy = y - cy;
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                x = dx * cos - dy * sin + cx;
                y = dx * sin + dy * cos + cy;
                break;
            }
            case "scale": {
                const sx = values[0] || 1;
                const sy = values[1] !== undefined ? values[1] : sx;
                x *= sx;
                y *= sy;
                break;
            }
            case "matrix": {
                if (values.length >= 6) {
                    const [a, b, c, d, e, f] = values;
                    const newX = a * x + c * y + e;
                    const newY = b * x + d * y + f;
                    x = newX;
                    y = newY;
                }
                break;
            }
        }
    }

    return { x, y };
}

function calculateRectCenter(attrs: Record<string, string>): { x: number; y: number } | null {
    const x = parseFloat(attrs.x || "0");
    const y = parseFloat(attrs.y || "0");
    const width = parseFloat(attrs.width || "0");
    const height = parseFloat(attrs.height || "0");
    
    if (isNaN(width) || isNaN(height)) return null;
    
    const center = {
        x: x + width / 2,
        y: y + height / 2,
    };
    
    // Apply transform if present
    if (attrs.transform) {
        return applyTransformToPoint(center, attrs.transform);
    }
    
    return center;
}

function calculateCircleCenter(attrs: Record<string, string>): { x: number; y: number } | null {
    const cx = parseFloat(attrs.cx || "0");
    const cy = parseFloat(attrs.cy || "0");
    
    if (isNaN(cx) || isNaN(cy)) return null;
    
    const center = { x: cx, y: cy };
    
    // Apply transform if present
    if (attrs.transform) {
        return applyTransformToPoint(center, attrs.transform);
    }
    
    return center;
}

function calculateEllipseCenter(attrs: Record<string, string>): { x: number; y: number } | null {
    const cx = parseFloat(attrs.cx || "0");
    const cy = parseFloat(attrs.cy || "0");
    
    if (isNaN(cx) || isNaN(cy)) return null;
    
    const center = { x: cx, y: cy };
    
    // Apply transform if present
    if (attrs.transform) {
        return applyTransformToPoint(center, attrs.transform);
    }
    
    return center;
}

function calculatePathCenter(d: string, transform?: string): { x: number; y: number } | null {
    if (!d) return null;
    
    try {
        const svgNs = "http://www.w3.org/2000/svg";
        const svg = document.createElementNS(svgNs, "svg");
        // Set a large viewBox to ensure getBBox() works correctly
        // This doesn't affect the coordinates returned by getBBox()
        svg.setAttribute("viewBox", "-10000 -10000 20000 20000");
        svg.style.position = "absolute";
        svg.style.width = "0";
        svg.style.height = "0";
        svg.style.overflow = "visible";
        svg.style.visibility = "hidden";
        svg.style.pointerEvents = "none";
        
        const path = document.createElementNS(svgNs, "path");
        path.setAttribute("d", d);
        if (transform) {
            path.setAttribute("transform", transform);
        }
        svg.appendChild(path);
        
        // Append to document body temporarily so getBBox() works
        // getBBox() requires the element to be in the DOM and rendered
        document.body.appendChild(svg);
        const bbox = path.getBBox();
        document.body.removeChild(svg);
        
        // getBBox() already accounts for transforms, so we don't need to apply it again
        return {
            x: bbox.x + bbox.width / 2,
            y: bbox.y + bbox.height / 2,
        };
    } catch (error) {
        console.warn("Failed to calculate path center:", error);
        return null;
    }
}

function calculatePolygonCenter(points: string, transform?: string): { x: number; y: number } | null {
    if (!points) return null;
    
    const coords = points
        .trim()
        .split(/[\s,]+/)
        .map(parseFloat)
        .filter((n) => !isNaN(n));
    
    if (coords.length < 4 || coords.length % 2 !== 0) return null;
    
    let sumX = 0;
    let sumY = 0;
    for (let i = 0; i < coords.length; i += 2) {
        sumX += coords[i];
        sumY += coords[i + 1];
    }
    
    const center = {
        x: sumX / (coords.length / 2),
        y: sumY / (coords.length / 2),
    };
    
    // Apply transform if present
    if (transform) {
        return applyTransformToPoint(center, transform);
    }
    
    return center;
}

function calculatePolylineCenter(points: string, transform?: string): { x: number; y: number } | null {
    return calculatePolygonCenter(points, transform);
}

function calculateLineCenter(attrs: Record<string, string>): { x: number; y: number } | null {
    const x1 = parseFloat(attrs.x1 || "0");
    const y1 = parseFloat(attrs.y1 || "0");
    const x2 = parseFloat(attrs.x2 || "0");
    const y2 = parseFloat(attrs.y2 || "0");
    
    if (isNaN(x1) || isNaN(y1) || isNaN(x2) || isNaN(y2)) return null;
    
    const center = {
        x: (x1 + x2) / 2,
        y: (y1 + y2) / 2,
    };
    
    // Apply transform if present
    if (attrs.transform) {
        return applyTransformToPoint(center, attrs.transform);
    }
    
    return center;
}

/**
 * Applies falloff curve to calculate intensity based on distance.
 */
export function applyFalloffCurve(
    distance: number,
    size: number,
    curveShape: CurveShape
): number {
    if (size <= 0) return 0;
    // Strictly cut off at size boundary - no effect beyond
    if (distance >= size) return 0;
    
    const normalizedDistance = distance / size;
    
    switch (curveShape) {
        case "linear":
            return 1 - normalizedDistance;
        case "exponential":
            // Exponential falloff
            return Math.exp(-normalizedDistance * 2);
        case "ease-in-out":
            return normalizedDistance < 0.5
                ? 2 * normalizedDistance * normalizedDistance
                : 1 - Math.pow(-2 * normalizedDistance + 2, 2) / 2;
        case "quadratic":
            return 1 - normalizedDistance * normalizedDistance;
        default:
            return 1 - normalizedDistance;
    }
}

export type DistortionMode = "randomize" | "repel" | "attract";

export interface DistortionPoint {
    x: number;
    y: number;
    intensity: number;
    size: number;
    curveShape: CurveShape;
    mode: DistortionMode;
}

/**
 * Applies distortion to shape elements based on distortion points.
 */
export function applyDistortionToShapes(
    shapes: ShapeElement[],
    points: DistortionPoint[]
): Array<{ type: string; element: string }> {
    // Simple seeded random function - seed based on shape position for uniqueness
    function createRandom(seed: number) {
        let currentSeed = seed;
        return function random() {
            currentSeed = (currentSeed * 9301 + 49297) % 233280;
            return currentSeed / 233280;
        };
    }
    
    // Track which shapes are selected for each point
    const pointSelections: Array<{
        point: { x: number; y: number; size: number; intensity: number };
        selectedShapes: Array<{
            index: number;
            type: string;
            center: { x: number; y: number };
            distance: number;
        }>;
    }> = points.map((point) => ({
        point: {
            x: point.x,
            y: point.y,
            size: point.size,
            intensity: point.intensity,
        },
        selectedShapes: [],
    }));
    
    const results = shapes.map((shape, shapeIdx) => {
        // Create unique random function for this shape based on its position
        const random = createRandom(
            Math.floor(shape.center.x * 1000 + shape.center.y * 1000 + shapeIdx * 1000)
        );
        
        let offsetX = 0;
        let offsetY = 0;
        
        // Calculate total offset from all points (additive)
        for (let pointIdx = 0; pointIdx < points.length; pointIdx++) {
            const point = points[pointIdx];
            const dx = shape.center.x - point.x;
            const dy = shape.center.y - point.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Skip shapes outside the size radius entirely
            if (distance >= point.size) {
                continue;
            }
            
            // Track this shape as selected for this point
            pointSelections[pointIdx].selectedShapes.push({
                index: shapeIdx,
                type: shape.type,
                center: { x: shape.center.x, y: shape.center.y },
                distance: distance,
            });
            
            // Calculate falloff (0-1, where 1 is at the point, 0 is at the edge)
            const falloff = applyFalloffCurve(distance, point.size, point.curveShape);
            
            // Scale intensity based on size parameter
            // Intensity (0-100) represents percentage of the size radius
            // So at 100% intensity, max offset is the full size radius
            const maxOffset = (point.intensity / 100) * point.size;
            
            // Apply falloff to get the actual intensity for this distance
            // Closer elements get higher intensity (more effect)
            const actualIntensity = maxOffset * falloff;
            
            let pointOffsetX = 0;
            let pointOffsetY = 0;
            
            switch (point.mode) {
                case "randomize": {
                    // Random offset in X and Y directions
                    pointOffsetX = (random() * 2 - 1) * actualIntensity;
                    pointOffsetY = (random() * 2 - 1) * actualIntensity;
                    break;
                }
                case "repel": {
                    // Push elements away from the point
                    // Direction vector from point to shape
                    if (distance > 0) {
                        const dirX = dx / distance; // Normalized direction
                        const dirY = dy / distance;
                        pointOffsetX = dirX * actualIntensity;
                        pointOffsetY = dirY * actualIntensity;
                    }
                    break;
                }
                case "attract": {
                    // Pull elements toward the point
                    // Direction vector from shape to point (opposite of repel)
                    if (distance > 0) {
                        const dirX = -dx / distance; // Normalized direction (toward point)
                        const dirY = -dy / distance;
                        pointOffsetX = dirX * actualIntensity;
                        pointOffsetY = dirY * actualIntensity;
                    }
                    break;
                }
            }
            
            offsetX += pointOffsetX;
            offsetY += pointOffsetY;
        }
        
        // Apply offset to shape based on type
        return {
            type: shape.type,
            element: applyOffsetToShape(shape, offsetX, offsetY),
        };
    });
    
    return results;
}

function applyOffsetToShape(
    shape: ShapeElement,
    offsetX: number,
    offsetY: number
): string {
    const attrs = { ...shape.attributes };
    
    switch (shape.type) {
        case "rect": {
            const x = parseFloat(attrs.x || "0") + offsetX;
            const y = parseFloat(attrs.y || "0") + offsetY;
            return buildElementString("rect", { ...attrs, x: String(x), y: String(y) });
        }
        case "circle": {
            const cx = parseFloat(attrs.cx || "0") + offsetX;
            const cy = parseFloat(attrs.cy || "0") + offsetY;
            return buildElementString("circle", { ...attrs, cx: String(cx), cy: String(cy) });
        }
        case "ellipse": {
            const cx = parseFloat(attrs.cx || "0") + offsetX;
            const cy = parseFloat(attrs.cy || "0") + offsetY;
            return buildElementString("ellipse", { ...attrs, cx: String(cx), cy: String(cy) });
        }
        case "path": {
            // For paths, we use transform to apply the offset
            // Preserve existing transform if present
            const existingTransform = attrs.transform || "";
            const newTransform = `translate(${offsetX}, ${offsetY})${existingTransform ? ` ${existingTransform}` : ""}`;
            return buildElementString("path", {
                ...attrs,
                transform: newTransform,
            });
        }
        case "polygon":
        case "polyline": {
            if (!attrs.points) return shape.originalElement;
            const coords = attrs.points
                .trim()
                .split(/[\s,]+/)
                .map(parseFloat);
            const newPoints = coords
                .map((coord, idx) => {
                    if (idx % 2 === 0) {
                        return coord + offsetX;
                    } else {
                        return coord + offsetY;
                    }
                })
                .join(" ");
            return buildElementString(shape.type, { ...attrs, points: newPoints });
        }
        case "line": {
            const x1 = parseFloat(attrs.x1 || "0") + offsetX;
            const y1 = parseFloat(attrs.y1 || "0") + offsetY;
            const x2 = parseFloat(attrs.x2 || "0") + offsetX;
            const y2 = parseFloat(attrs.y2 || "0") + offsetY;
            return buildElementString("line", {
                ...attrs,
                x1: String(x1),
                y1: String(y1),
                x2: String(x2),
                y2: String(y2),
            });
        }
        default:
            return shape.originalElement;
    }
}

function buildElementString(
    tagName: string,
    attributes: Record<string, string>
): string {
    const attrs = Object.entries(attributes)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}="${value}"`)
        .join(" ");
    if (attrs) {
        return `<${tagName} ${attrs} />`;
    }
    return `<${tagName} />`;
}

/**
 * Parses SVG content and extracts all editable elements.
 * Returns an array of SvgElement objects with their positions and attributes.
 */
export interface ParsedSvgElement {
    id: string;
    type: string;
    originalElement: string;
    startIndex: number;
    endIndex: number;
    transform: string;
    attributes: Record<string, string>;
}

const MAX_PARSED_ELEMENTS = 500; // Limit to prevent performance issues

export function parseSvgElements(svgContent: string): ParsedSvgElement[] {
    const elements: ParsedSvgElement[] = [];
    const elementTypes = [
        "rect",
        "circle",
        "ellipse",
        "path",
        "polygon",
        "polyline",
        "line",
    ];

    for (const type of elementTypes) {
        // Match self-closing tags: <type ... />
        const selfClosingRegex = new RegExp(`<${type}([^>]*?)(?:\\s*/>|>)`, "gi");
        let match;
        while ((match = selfClosingRegex.exec(svgContent)) !== null) {
            // Stop parsing if we've reached the limit
            if (elements.length >= MAX_PARSED_ELEMENTS) {
                break;
            }

            const attrsString = match[1] || "";
            const fullMatch = match[0];
            const startIndex = match.index;
            const endIndex = startIndex + fullMatch.length;

            const attributes = parseAttributes(attrsString);
            const existingTransform = attributes.transform || "";

            const id = `${type}-${startIndex}-${Date.now()}-${Math.random()}`;

            elements.push({
                id,
                type,
                originalElement: fullMatch,
                startIndex,
                endIndex,
                transform: existingTransform,
                attributes,
            });
        }
        
        // Stop if we've reached the limit
        if (elements.length >= MAX_PARSED_ELEMENTS) {
            break;
        }
    }

    return elements;
}

export function getParsedElementCount(svgContent: string): number {
    const elementTypes = [
        "rect",
        "circle",
        "ellipse",
        "path",
        "polygon",
        "polyline",
        "line",
    ];

    let count = 0;
    for (const type of elementTypes) {
        const regex = new RegExp(`<${type}([^>]*?)(?:\\s*/>|>)`, "gi");
        const matches = svgContent.match(regex);
        if (matches) {
            count += matches.length;
        }
    }
    return count;
}


/**
 * Converts position string to normalized coordinates (0-1) within grid bounds.
 */
export function positionToCoordinates(
    position: string,
    cols: number,
    rows: number
): { x: number; y: number } {
    const positionMap: Record<string, { x: number; y: number }> = {
        "top-left": { x: 0, y: 0 },
        top: { x: 0.5, y: 0 },
        "top-right": { x: 1, y: 0 },
        left: { x: 0, y: 0.5 },
        center: { x: 0.5, y: 0.5 },
        right: { x: 1, y: 0.5 },
        "bottom-left": { x: 0, y: 1 },
        bottom: { x: 0.5, y: 1 },
        "bottom-right": { x: 1, y: 1 },
    };

    return positionMap[position] || { x: 0.5, y: 0.5 };
}

/**
 * Calculates linear gradient probability for a cell.
 * Returns 0-1 probability based on distance along line from start to end position.
 */
export function calculateLinearGradientProbability(
    col: number,
    row: number,
    startPos: string,
    endPos: string,
    cols: number,
    rows: number
): number {
    const start = positionToCoordinates(startPos, cols, rows);
    const end = positionToCoordinates(endPos, cols, rows);

    // Convert cell position to normalized coordinates (0-1)
    const cellX = col / (cols - 1 || 1);
    const cellY = row / (rows - 1 || 1);

    // Calculate vector from start to end
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lineLength = Math.sqrt(dx * dx + dy * dy);

    if (lineLength === 0) return 0.5; // Start and end are the same

    // Calculate vector from start to cell
    const cellDx = cellX - start.x;
    const cellDy = cellY - start.y;

    // Project cell position onto the line
    const dot = cellDx * dx + cellDy * dy;
    const projectionLength = dot / lineLength;
    const normalizedDistance = projectionLength / lineLength;

    // Clamp and invert (closer to start = higher probability)
    return Math.max(0, Math.min(1, 1 - normalizedDistance));
}

/**
 * Calculates Perlin noise probability for a cell.
 * Returns 0-1 probability based on Perlin noise value at cell position.
 */
export function calculatePerlinNoiseProbability(
    col: number,
    row: number,
    cols: number,
    rows: number,
    scale: number = 0.1
): number {
    // Normalize cell position to 0-1 range
    const normalizedX = col / (cols - 1 || 1);
    const normalizedY = row / (rows - 1 || 1);
    
    // Apply scale to control noise frequency
    const noiseX = normalizedX * scale * cols;
    const noiseY = normalizedY * scale * rows;
    
    // Get Perlin noise value (0-1)
    return noise2DNormalized(noiseX, noiseY);
}

/**
 * Calculates radial gradient probability for a cell.
 * Returns 0-1 probability based on radial distance from start position.
 */
export function calculateRadialGradientProbability(
    col: number,
    row: number,
    startPos: string,
    cols: number,
    rows: number
): number {
    const start = positionToCoordinates(startPos, cols, rows);

    // Convert cell position to normalized coordinates (0-1)
    const cellX = col / (cols - 1 || 1);
    const cellY = row / (rows - 1 || 1);

    // Calculate distance from start position
    const dx = cellX - start.x;
    const dy = cellY - start.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Maximum possible distance (corner to corner)
    const maxDistance = Math.sqrt(2);

    // Normalize and invert (closer = higher probability)
    const normalizedDistance = distance / maxDistance;
    return Math.max(0, Math.min(1, 1 - normalizedDistance));
}

/**
 * Radial gradient with weight inverted: farther from start = higher probability.
 */
export function calculateReverseRadialGradientProbability(
    col: number,
    row: number,
    startPos: string,
    cols: number,
    rows: number
): number {
    return (
        1 -
        calculateRadialGradientProbability(col, row, startPos, cols, rows)
    );
}

/**
 * Applies noise to a probability value.
 * Adds random variation between -noise/100 and +noise/100, clamped to 0-1.
 */
export function applyNoiseToProbability(
    probability: number,
    noise: number
): number {
    if (noise === 0) return probability;

    const noiseAmount = (noise / 100) * (Math.random() * 2 - 1); // -noise/100 to +noise/100
    return Math.max(0, Math.min(1, probability + noiseAmount));
}

/**
 * 4x4 Bayer dithering matrix for ordered dithering.
 * This creates a spatial pattern that distributes values more evenly.
 */
const BAYER_MATRIX_4X4 = [
    [0, 8, 2, 10],
    [12, 4, 14, 6],
    [3, 11, 1, 9],
    [15, 7, 13, 5],
];

/**
 * Gets the dithering threshold value for a given cell position using Bayer matrix.
 */
function getBayerThreshold(col: number, row: number): number {
    const matrixSize = 4;
    const matrixX = col % matrixSize;
    const matrixY = row % matrixSize;
    const threshold = BAYER_MATRIX_4X4[matrixY][matrixX];
    // Normalize to 0-1 range (matrix values are 0-15)
    return threshold / 16;
}

/**
 * Applies ordered dithering to a probability value based on spatial position.
 * Uses a Bayer matrix pattern for consistent, spatially-distributed dithering.
 */
export function applyDitheringToProbability(
    probability: number,
    dithering: number,
    col: number,
    row: number
): number {
    if (dithering === 0) return probability;

    // Get Bayer threshold value for this cell position
    const threshold = getBayerThreshold(col, row);
    
    // Convert threshold (0-1) to dithering offset (-1 to 1)
    // Threshold of 0.5 (middle) gives no offset, extremes give max offset
    const ditherValue = (threshold - 0.5) * 2; // -1 to 1
    
    // Apply dithering amount
    const ditherAmount = (dithering / 100) * ditherValue;
    
    return Math.max(0, Math.min(1, probability + ditherAmount));
}

/**
 * Generates a grid pattern SVG from an object SVG.
 * Uses <use> elements with <defs> to repeat the object SVG in a grid.
 * Supports density, fill patterns, and noise for selective cell filling.
 */
export function generateGridPattern(
    objectSvgContent: string,
    columns: number,
    rows: number,
    cellWidth: number,
    cellHeight: number,
    cellSpacingX: number = 0,
    cellSpacingY: number = 0,
    density: number = 100,
    fillPattern:
        | "even"
        | "linear-gradient"
        | "radial-gradient"
        | "reverse-radial-gradient"
        | "perlin-noise" = "even",
    startPosition: string = "top-left",
    endPosition: string = "bottom-right",
    noise: number = 0,
    dithering: number = 0
): string {
    // Extract inner content from object SVG (remove <svg> tags)
    const innerMatch = objectSvgContent.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
    const innerContent = innerMatch ? innerMatch[1] : objectSvgContent;

    // Extract viewBox from object SVG
    const viewBoxInfo = extractViewBox(objectSvgContent);
    const objectViewBox = viewBoxInfo?.viewBox || "0 0 100 100";
    
    // Parse viewBox to get dimensions for scaling
    const viewBoxParts = objectViewBox.split(/\s+/).map(Number);
    const [, , viewBoxWidth, viewBoxHeight] = viewBoxParts.length === 4 ? viewBoxParts : [0, 0, 100, 100];

    // Calculate grid dimensions (including spacing between cells)
    const gridWidth = columns * cellWidth + (columns - 1) * cellSpacingX;
    const gridHeight = rows * cellHeight + (rows - 1) * cellSpacingY;
    const gridViewBox = `0 0 ${gridWidth} ${gridHeight}`;

    // Calculate cell positions and probabilities
    interface CellInfo {
        col: number;
        row: number;
        probability: number;
    }

    const cells: CellInfo[] = [];
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            let probability = 1;

            // Calculate probability based on fill pattern
            if (fillPattern === "even") {
                probability = 1;
            } else if (fillPattern === "linear-gradient") {
                probability = calculateLinearGradientProbability(
                    col,
                    row,
                    startPosition,
                    endPosition,
                    columns,
                    rows
                );
            } else if (fillPattern === "radial-gradient") {
                probability = calculateRadialGradientProbability(
                    col,
                    row,
                    startPosition,
                    columns,
                    rows
                );
            } else if (fillPattern === "reverse-radial-gradient") {
                probability = calculateReverseRadialGradientProbability(
                    col,
                    row,
                    startPosition,
                    columns,
                    rows
                );
            } else if (fillPattern === "perlin-noise") {
                probability = calculatePerlinNoiseProbability(
                    col,
                    row,
                    columns,
                    rows
                );
            }

            // Apply noise
            probability = applyNoiseToProbability(probability, noise);

            // Apply dithering (spatial variation)
            probability = applyDitheringToProbability(
                probability,
                dithering,
                col,
                row
            );

            cells.push({ col, row, probability });
        }
    }

    // Sort cells by probability (descending) and select top N% based on density
    const totalCells = cells.length;
    const cellsToFill = Math.floor((density / 100) * totalCells);
    const sortedCells = [...cells].sort((a, b) => b.probability - a.probability);
    const selectedCells = sortedCells.slice(0, cellsToFill);

    // Create a Set for fast lookup
    const selectedCellSet = new Set<string>();
    for (const cell of selectedCells) {
        selectedCellSet.add(`${cell.col},${cell.row}`);
    }

    // Generate inline elements for selected cells (replacing <use> with actual content)
    const cellElements: string[] = [];
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            if (selectedCellSet.has(`${col},${row}`)) {
                // Calculate position with spacing
                const x = col * (cellWidth + cellSpacingX);
                const y = row * (cellHeight + cellSpacingY);
                
                // Calculate scale to fit content in cell
                const scaleX = cellWidth / viewBoxWidth;
                const scaleY = cellHeight / viewBoxHeight;
                
                // Create transform: scale first (from origin), then translate to position
                // After scaling, coordinates are scaled, so we need to adjust translation
                const transform = `scale(${scaleX}, ${scaleY}) translate(${x / scaleX}, ${y / scaleY})`;
                
                // Wrap inner content in a group with transform
                cellElements.push(
                    `<g transform="${transform}">${innerContent}</g>`
                );
            }
        }
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${gridViewBox}">
  ${cellElements.join("\n  ")}
</svg>`;
}

/**
 * Converts a hex color string to RGB components.
 * @param hex - Hex color string (e.g., "#ff0000" or "ff0000")
 * @returns Object with r, g, b values (0-255)
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

/**
 * Converts RGB components to hex color string.
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Hex color string (e.g., "#ff0000")
 */
function rgbToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b].map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }).join("")}`;
}

/**
 * Interpolates between two hex colors.
 * @param color1 - First hex color string
 * @param color2 - Second hex color string
 * @param t - Interpolation factor (0-1), where 0 = color1, 1 = color2
 * @returns Interpolated hex color string
 */
export function interpolateColor(
    color1: string,
    color2: string,
    t: number
): string {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    
    if (!rgb1 || !rgb2) {
        // Fallback to color1 if parsing fails
        return color1;
    }
    
    const r = rgb1.r + (rgb2.r - rgb1.r) * t;
    const g = rgb1.g + (rgb2.g - rgb1.g) * t;
    const b = rgb1.b + (rgb2.b - rgb1.b) * t;
    
    return rgbToHex(r, g, b);
}

/**
 * Gets a color from a gradient based on position and max steps.
 * @param colors - Array of gradient color hex strings (minimum 2)
 * @param position - Position along gradient (0-1)
 * @param maxSteps - Maximum number of gradient steps (for quantization)
 * @returns Hex color string
 */
export function getGradientColor(
    colors: string[],
    position: number,
    maxSteps: number
): string {
    if (colors.length < 2) {
        return colors[0] || "#000000";
    }
    
    // Clamp position to 0-1
    const clampedPosition = Math.max(0, Math.min(1, position));
    
    // Quantize position based on maxSteps
    const quantizedPosition =
        maxSteps > 1
            ? Math.round(clampedPosition * (maxSteps - 1)) / (maxSteps - 1)
            : clampedPosition;
    
    // Map position to color segment
    const segmentSize = 1 / (colors.length - 1);
    const segmentIndex = Math.min(
        Math.floor(quantizedPosition / segmentSize),
        colors.length - 2
    );
    
    const segmentStart = segmentIndex * segmentSize;
    const segmentPosition = (quantizedPosition - segmentStart) / segmentSize;
    
    return interpolateColor(colors[segmentIndex], colors[segmentIndex + 1], segmentPosition);
}

/**
 * Picks one palette color per path segment (by `pathIndex`). Deterministic so all
 * placements on the same path share a color and previews/exports stay stable.
 */
export function paletteColorForPathIndex(
    colors: string[],
    pathIndex: number
): string {
    if (colors.length === 0) return "#000000";
    if (colors.length === 1) return colors[0];

    let h =
        Math.imul(pathIndex | 0, 0x9e3779b1) ^
        Math.imul(pathIndex | 0, 0x85ebca6b) ^
        0x4f2d72a1;
    h >>>= 0;
    h ^= h >>> 16;
    h = Math.imul(h, 0x7feb352d);
    h ^= h >>> 15;
    h = Math.imul(h, 0x846ca68b);
    h ^= h >>> 16;
    return colors[(h >>> 0) % colors.length];
}
